import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  getAdminProspectResponseNotificationUnreadCount,
  listAdminProspectResponseNotifications,
  markAdminProspectResponseNotificationsRead,
} from '../../services/admin/prospectResponseNotificationsService.js';
import { formatAdminDate } from './adminProspectDisplay.js';
import {
  ADMIN_PROSPECT_RESPONSE_NOTIFICATIONS_REFRESH_MS,
  adminProspectResponseNotificationIntentLabel,
  buildOpenResponseLinkState,
  createAdminProspectResponseNotificationsController,
} from './adminProspectResponseNotifications.js';
import { useAdminProspectResponseNotificationsView } from './adminProspectResponseNotificationsView.js';
import AdminProspectPushOptIn from './AdminProspectPushOptIn.jsx';
import {
  PROSPECT_PUSH_OFFER_TYPE,
} from '../../services/admin/prospectWebPushDisplay.js';
import {
  handleProspectPushOfferAsAdminConsumer,
  refreshNotificationsForPushOffer,
} from '../../services/admin/prospectWebPushForeground.js';

function formatNotificationPropertyLine(item) {
  const parts = [item?.propertyAddress, item?.propertyCity]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

export default function AdminProspectResponseNotificationsBell({ role }) {
  const { t, language } = useLanguage();
  const { currentUser, impersonation } = useAuth();
  const panelTitleId = useId();
  const listId = useId();

  const mountedRef = useRef(true);
  const authUidRef = useRef('');
  const roleRef = useRef(role);
  const openRef = useRef(false);
  const bellButtonRef = useRef(null);
  const panelRef = useRef(null);
  const impersonating = impersonation?.active === true;
  const impersonatingRef = useRef(impersonating);
  impersonatingRef.current = impersonating;

  authUidRef.current = currentUser?.uid ? String(currentUser.uid) : '';
  roleRef.current = role;

  const [controller] = useState(() =>
    createAdminProspectResponseNotificationsController({
      getMounted: () => mountedRef.current,
      getAuthUid: () => authUidRef.current,
      getRole: () => roleRef.current,
      listNotifications: listAdminProspectResponseNotifications,
      getUnreadCount: getAdminProspectResponseNotificationUnreadCount,
      markNotificationsRead: markAdminProspectResponseNotificationsRead,
    }),
  );

  const [open, setOpen] = useState(false);
  const view = useAdminProspectResponseNotificationsView(controller);

  openRef.current = open;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Auth/role changes invalidate and clear obsolete session-owned state.
  useEffect(() => {
    controller.invalidateSession();
    setOpen(false);
  }, [currentUser?.uid, role, controller]);

  const refreshUnread = useCallback((opts) => controller.refreshUnread(opts), [controller]);
  const loadList = useCallback((opts) => controller.loadList(opts), [controller]);

  useEffect(() => {
    if (role === 'super_admin' && currentUser?.uid) {
      refreshUnread();
    }
  }, [role, currentUser?.uid, refreshUnread]);

  // Bounded interval refresh while admin app active + focus/visibility revalidation.
  useEffect(() => {
    if (role !== 'super_admin' || !currentUser?.uid) return undefined;

    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refreshUnread();
      if (openRef.current) {
        loadList({ soft: true });
      }
    };

    const intervalId = setInterval(tick, ADMIN_PROSPECT_RESPONSE_NOTIFICATIONS_REFRESH_MS);

    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) tick();
    };
    const onFocus = () => tick();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [role, currentUser?.uid, refreshUnread, loadList]);

  // Sole foreground consumer: ACK only after a visible focused eligible session
  // successfully applies the notification refresh (controller outcomes inspected).
  useEffect(() => {
    if (role !== 'super_admin' || !currentUser?.uid) return undefined;
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return undefined;

    const onMessage = (event) => {
      const data = event?.data;
      if (!data || data.type !== PROSPECT_PUSH_OFFER_TYPE) return;
      void handleProspectPushOfferAsAdminConsumer({
        offer: data,
        getDocumentState: () => ({
          visibilityState:
            typeof document !== 'undefined' ? document.visibilityState : 'hidden',
          hasFocus:
            typeof document !== 'undefined' && typeof document.hasFocus === 'function'
              ? document.hasFocus()
              : false,
        }),
        // Live refs — never freeze role/uid/impersonation from the effect closure.
        getSessionState: () => ({
          role: roleRef.current,
          impersonating: impersonatingRef.current,
          authUid: authUidRef.current,
          mounted: mountedRef.current,
        }),
        handleOffer: async () =>
          refreshNotificationsForPushOffer({
            refreshUnread: () => controller.refreshUnread(),
            loadList: (opts) => controller.loadList(opts),
            panelOpen: openRef.current,
          }),
        postAck: (ack) => {
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage(ack);
          }
        },
      });
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [role, currentUser?.uid, controller]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        controller.bumpListGeneration();
        setOpen(false);
        bellButtonRef.current?.focus();
      }
    };
    const onPointerDown = (event) => {
      const target = event.target;
      if (
        panelRef.current?.contains(target) ||
        bellButtonRef.current?.contains(target)
      ) {
        return;
      }
      controller.bumpListGeneration();
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, controller]);

  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.focus();
    }
  }, [open]);

  if (role !== 'super_admin') {
    return null;
  }

  const toggleOpen = () => {
    const next = !open;
    // Network side effects stay outside React state-updater callbacks.
    setOpen(next);
    if (next) {
      loadList({ append: false });
      refreshUnread();
    } else {
      controller.bumpListGeneration();
    }
  };

  const handleMarkRead = (notificationIds) => {
    void controller.markRead(notificationIds);
  };

  const {
    items,
    nextPageToken,
    configured,
    unreadCount,
    listStatus,
    errorKey,
    markingIds,
  } = view;

  const badgeLabel =
    unreadCount > 0
      ? t('admin.notifications.unreadBadge').replace('{count}', String(unreadCount))
      : null;

  return (
    <div className="relative shrink-0">
      <button
        ref={bellButtonRef}
        type="button"
        onClick={toggleOpen}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
        aria-label={t('admin.notifications.bellLabel')}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? listId : undefined}
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
        {unreadCount > 0 ? (
          <span
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold leading-none text-white"
            aria-label={badgeLabel}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          ref={panelRef}
          id={listId}
          role="dialog"
          aria-modal="false"
          aria-labelledby={panelTitleId}
          tabIndex={-1}
          className="absolute right-0 z-50 mt-2 w-[min(100vw-1.5rem,22rem)] rounded-lg border border-slate-200 bg-white shadow-lg outline-none"
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
            <h2 id={panelTitleId} className="text-sm font-semibold text-slate-900">
              {t('admin.notifications.panelTitle')}
            </h2>
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-800"
              onClick={() => {
                controller.bumpListGeneration();
                setOpen(false);
                bellButtonRef.current?.focus();
              }}
            >
              {t('admin.notifications.close')}
            </button>
          </div>

          <AdminProspectPushOptIn role={role} />

          <div className="max-h-80 overflow-y-auto px-3 py-2" aria-busy={listStatus === 'loading'}>
            {listStatus === 'loading' ? (
              <p className="py-4 text-sm text-slate-500">{t('admin.notifications.loading')}</p>
            ) : null}

            {errorKey ? (
              <div className="py-2" role="alert">
                <p className="text-sm text-rose-700">{t(errorKey)}</p>
                <button
                  type="button"
                  className="mt-1 text-xs font-medium text-indigo-700 underline-offset-2 hover:underline"
                  onClick={() => loadList({ append: false })}
                >
                  {t('admin.notifications.retry')}
                </button>
              </div>
            ) : null}

            {configured === false ? (
              <p className="py-4 text-sm text-slate-600">{t('admin.notifications.unconfigured')}</p>
            ) : null}

            {configured !== false && listStatus === 'ready' && items.length === 0 ? (
              <p className="py-4 text-sm text-slate-600">{t('admin.notifications.empty')}</p>
            ) : null}

            {configured !== false && items.length > 0 ? (
              <ul className="space-y-2" role="list">
                {items.map((item) => {
                  const pathname = `/admin/prospection/${item.prospectId || ''}`;
                  const linkState = buildOpenResponseLinkState(item);
                  const busy = markingIds.has(item.id);
                  const propertyLine = formatNotificationPropertyLine(item);
                  return (
                    <li
                      key={item.id || item.eventId}
                      className={`rounded-md border px-2.5 py-2 ${
                        item.unread
                          ? 'border-indigo-200 bg-indigo-50/60'
                          : 'border-slate-100 bg-slate-50/50'
                      }`}
                    >
                      <p className="text-sm font-medium text-slate-900">
                        {adminProspectResponseNotificationIntentLabel(item.intent, t)}
                      </p>
                      {propertyLine ? (
                        <p className="mt-0.5 text-xs text-slate-600">{propertyLine}</p>
                      ) : null}
                      <p className="mt-0.5 text-xs text-slate-500">
                        {item.submittedAt
                          ? formatAdminDate(item.submittedAt, language)
                          : '—'}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {item.prospectId ? (
                          <Link
                            to={pathname}
                            state={linkState}
                            className="text-xs font-medium text-indigo-700 underline-offset-2 hover:underline"
                            onClick={() => {
                              controller.bumpListGeneration();
                              setOpen(false);
                            }}
                          >
                            {t('admin.notifications.openResponse')}
                          </Link>
                        ) : null}
                        {item.unread ? (
                          <button
                            type="button"
                            disabled={busy}
                            className="text-xs text-slate-600 underline-offset-2 hover:underline disabled:opacity-50"
                            onClick={() => handleMarkRead([item.id])}
                          >
                            {t('admin.notifications.markRead')}
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>

          {configured !== false && nextPageToken ? (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-3 py-2">
              <button
                type="button"
                className="ml-auto text-xs font-medium text-indigo-700 hover:underline disabled:opacity-50"
                disabled={listStatus === 'loading-more'}
                onClick={() => loadList({ append: true, pageToken: nextPageToken })}
              >
                {t('admin.notifications.loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
